import React, { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { displayToastError } from '@/helpers';
import { cn } from '@/lib/utils';

export enum DropzoneStatus {
  IDLE = 'IDLE',
  UPLOADING = 'UPLOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

interface DropzoneProps {
  file: File | null;
  setFile: (file: File | null) => void;
  status?: DropzoneStatus;
  errorMessage?: string;
  errorActionLabel?: string;
  onErrorAction?: () => void;
  successMessage?: string;
  accept?: string; // for input accept attr (e.g. '.csv,.txt')
  maxSize?: number; // max size in MB
}

export function Dropzone({
  setFile,
  file,
  status = DropzoneStatus.IDLE,
  errorMessage,
  errorActionLabel,
  onErrorAction,
  successMessage,
  accept = '.csv',
  maxSize = 5, // default 5MB
}: DropzoneProps) {
  const { t } = useTranslation('common');
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const maxSizeBytes = maxSize * 1024 * 1024;

  // Helper to check file extension is allowed
  const isFormatValid = (fileName: string) => {
    const allowedFormats = accept.split(',').map(f => f.trim().toLowerCase());

    return allowedFormats.some(format => fileName.toLowerCase().endsWith(format));
  };

  const validateFile = (file: File) => {
    if (maxSizeBytes && file.size > maxSizeBytes) {
      displayToastError(
        t('fileTooLarge', {
          maxSize: maxSize,
        })
      );

      return false;
    }
    if (!isFormatValid(file.name)) {
      displayToastError(
        t('fileInvalidFormat', {
          formats: accept,
        })
      );
      return false;
    }
    return true;
  };

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        const droppedFile = e.dataTransfer.files[0];
        if (validateFile(droppedFile)) {
          setFile(droppedFile);
        } else {
          setFile(null);
        }
      }
    },
    [setFile, maxSizeBytes, accept]
  );

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleBrowseClick = () => {
    inputRef.current?.click();
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (validateFile(selectedFile)) {
        setFile(selectedFile);
      } else {
        setFile(null);
      }
    }
  };

  const renderStatusText = () => {
    switch (status) {
      case DropzoneStatus.SUCCESS:
        return <p className="text-success">{successMessage}</p>;
      case DropzoneStatus.ERROR:
        return (
          <p className="text-destructive">
            {errorMessage}
            {onErrorAction && errorActionLabel && (
              <>
                {/* eslint-disable-next-line i18next/no-literal-string */}
                {' · '}
                <button
                  type="button"
                  onClick={e => {
                    e.stopPropagation();
                    onErrorAction();
                  }}
                  className="underline"
                >
                  {errorActionLabel}
                </button>
              </>
            )}
          </p>
        );
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(
        'border-[1px] border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors bg-muted',
        dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/30'
      )}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onClick={handleBrowseClick}
    >
      {file && (
        <div className="flex items-center justify-between gap-2 text-start text-sm">
          <div className="overflow-hidden">
            <p className="truncate">{file?.name}</p>
            {renderStatusText()}
          </div>
          {status === DropzoneStatus.UPLOADING ? (
            <Button disabled variant="outline">
              {/* your loading icon */}
              Uploading...
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive/80"
              onClick={() => setFile(null)}
            >
              {t('fileUpload.deleteFile')}
            </Button>
          )}
        </div>
      )}

      {!file && (
        <>
          <input
            disabled={!!file}
            type="file"
            accept={accept}
            ref={inputRef}
            className="hidden"
            onChange={onFileChange}
          />
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              {/* your upload icon */}
              <div>
                <p className="text-start text-sm">{t('dragAndDrop')}</p>
                <p className="text-start text-sm text-muted-foreground">
                  {t('fileRequirements', {
                    maxSize: maxSize,
                    formats: accept,
                  })}
                </p>
              </div>
            </div>
            <Button variant="outline">{t('fileUpload.chooseFile')}</Button>
          </div>
        </>
      )}
    </div>
  );
}
